import { useEffect, useState } from "react";
import Layout from "../../components/Layout/Layout";
import {
  SearchIcon,
  FlagIcon,
  ExclamationIcon,
  CheckIcon,
} from "@heroicons/react/solid";
import Spinner from "../../utils/Spinner";
import Paginate from "../../utils/Paginate";
import TransactionUtilitiesServices from "../../services/Transaction_Utilities";
import { toast } from "react-toastify";
import ReportFraudulentAccountModal from "./reportAccount";
import { TransactionDetailsModal } from "./TransactionDetailsModal"; 
import ToggleFlagModal from "./ToggleFlagModal";
import UnflagConfirmationModal from "./UnflagConfirmationModal";

export function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export const FraudulentAccounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [pageIndex, setPageIndex] = useState(1);
  const [prevPage, setPrev] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPage, setTotalPage] = useState(0);
  const [nextPage, setNextPage] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [flagLoading, setFlagLoading] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [showTransactionsModal, setShowTransactionsModal] = useState(false);
  
  // New state for toggle flag modal
  const [toggleFlagModal, setToggleFlagModal] = useState({
    isOpen: false,
    accountNumber: "",
    currentFlagStatus: false,
    adminReason: "",
    bankCode: "105", // Default bank code
    bankName: "",
    existingAdminReason: "" // For viewing existing reason when unflagging
  });

  // New state for unflag confirmation modal
  const [unflagConfirmationModal, setUnflagConfirmationModal] = useState({
    isOpen: false,
    accountNumber: "",
    bankCode: "105",
    bankName: ""
  });

  const fetchFraudulentAccounts = async () => {
    setLoading(true);
    try {
      const response = await TransactionUtilitiesServices.listFraudulentAccount(
        20,
        pageIndex,
        search
      );

      if (response?.data) {
        setAccounts(response.data.result || []);
        setPageNumber(response.data.totalCount);
        setTotalPage(response.data.totalPages);
        setPrev(response.data.hasPreviousPage);
        setNextPage(response.data.hasNextPage);
      } else {
        setAccounts([]);
        toast.error("Failed to fetch fraudulent accounts");
      }
    } catch (error) {
      console.error("Error fetching fraudulent accounts:", error);
      setAccounts([]);
      toast.error(error.message || "Failed to fetch fraudulent accounts");
    } finally {
      setLoading(false);
    }
  };

  const handleViewTransactions = (account) => {
    setSelectedAccount(account);
    setShowTransactionsModal(true);
  };

  const handleReportAccount = async (formData) => {
    setLoading(true);
    try {
      const response =
        await TransactionUtilitiesServices.reportFraudulentAccount(formData);

      if (response?.status) {
        toast.success(response.message || "Account reported successfully");
        await fetchFraudulentAccounts();
        setShowReportModal(false);
      } else {
        toast.error(response?.data?.message || "Failed to report account");
      }
    } catch (error) {
      toast.error(error.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  // Open toggle flag modal instead of directly toggling
  const handleOpenToggleFlagModal = (account) => {
    // Use account's bankCode if available, otherwise use default "105"
    const bankCode = account.bankCode || "105";
    
    if (account.isFlagged) {
      // For unflagging - show confirmation modal
      setUnflagConfirmationModal({
        isOpen: true,
        accountNumber: account.accountNumber,
        bankCode: bankCode,
        bankName: account.bankName || "N/A"
      });
    } else {
      // For flagging - show flag modal with form
      setToggleFlagModal({
        isOpen: true,
        accountNumber: account.accountNumber,
        currentFlagStatus: account.isFlagged,
        adminReason: "",
        bankCode: bankCode,
        bankName: account.bankName || "N/A",
        existingAdminReason: account.adminReason || ""
      });
    }
  };

  // Handle the actual flag toggle with reason
  const handleToggleFlag = async () => {
    const { accountNumber, currentFlagStatus, adminReason, bankCode } = toggleFlagModal;
    
    if (!adminReason.trim()) {
      toast.error("Please provide a reason for flagging");
      return;
    }

    setFlagLoading(accountNumber);
    try {
      const newFlagStatus = !currentFlagStatus;
      const response = await TransactionUtilitiesServices.toggleFlagStatus(
        accountNumber,
        newFlagStatus,
        adminReason,
        bankCode
      );

      if (response?.status) {
        toast.success(response.message || "Flag status updated successfully");
        await fetchFraudulentAccounts();
        setToggleFlagModal({
          isOpen: false,
          accountNumber: "",
          currentFlagStatus: false,
          adminReason: "",
          bankCode: "105",
          bankName: "",
          existingAdminReason: ""
        });
      } else {
        toast.error(response?.data?.message || "Failed to update flag status");
      }
    } catch (error) {
      toast.error(error.message || "An error occurred");
    } finally {
      setFlagLoading(null);
    }
  };

  // Handle unflagging without reason
  const handleUnflagAccount = async () => {
    const { accountNumber, bankCode } = unflagConfirmationModal;
    
    setFlagLoading(accountNumber);
    try {
      const response = await TransactionUtilitiesServices.toggleFlagStatus(
        accountNumber,
        false, // Set to false for unflagging
        "Account unflagged by admin", // Default reason for unflagging
        bankCode
      );

      if (response?.status) {
        toast.success(response.message || "Account unflagged successfully");
        await fetchFraudulentAccounts();
        setUnflagConfirmationModal({
          isOpen: false,
          accountNumber: "",
          bankCode: "105",
          bankName: ""
        });
      } else {
        toast.error(response?.data?.message || "Failed to unflag account");
      }
    } catch (error) {
      toast.error(error.message || "An error occurred");
    } finally {
      setFlagLoading(null);
    }
  };

  const handleSearch = (text) => {
    setPageIndex(1);
    setSearch(text);
  };

  const handlePageChange = (newPage) => {
    setPageIndex(newPage);
  };

  useEffect(() => {
    fetchFraudulentAccounts();
  }, [pageIndex, search]);

  return (
    <Layout
      title="Transaction Utilities - Fraudulent Accounts"
      pageTag="transaction_utilities"
      current="/utilities"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
        <div className="flex justify-between items-center mt-10">
          <h1 className="text-2xl font-bold text-gray-900">
            Fraudulent Accounts
          </h1>
          {/* <button
            onClick={() => setShowReportModal(true)}
            className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
            Report Account
          </button> */}
        </div>

        <div className="mt-10">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-end">
            <div className="relative flex-grow">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <SearchIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search by account number"
                className="pl-10 block w-full sm:text-sm border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                onChange={(e) => handleSearch(e.target.value)}
                value={search}
              />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="mt-8 flex justify-center">
            <Spinner />
          </div>
        ) : accounts.length === 0 ? (
          <div className="mt-8 text-center bg-white p-8 rounded-lg shadow">
            <div className="text-gray-500 text-lg">
              {search
                ? "No accounts match your search criteria"
                : "No fraudulent accounts found"}
            </div>
          </div>
        ) : (
          <>
            <div className="mt-8 overflow-x-auto">
              <div className="inline-block min-w-full align-middle">
                <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          Account Number
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          Status
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          Report
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          Last Reported
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          Admin Reason
                        </th>
                          <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          Bank Name
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          Transactions
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {accounts.map((account) => (
                        <tr key={account.accountNumber}>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {account.accountNumber}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {account.isFlagged ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                <FlagIcon className="h-4 w-4 mr-1" />
                                Flagged
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                <ExclamationIcon className="h-4 w-4 mr-1" />
                                Reported
                              </span>
                            )}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {account.numberOfTimesReported}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {new Date(account.lastReportedAt).toLocaleString()}
                          </td>
                          <td className="px-3 py-4 text-sm text-gray-500 max-w-xs">
                            <div className="">
                              {account.adminReason || "N/A"}
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {account.bankName || "N/A"}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            <button
                              onClick={() => handleViewTransactions(account)}
                              className="text-blue-600 hover:text-blue-800 mr-3"
                              title="View transaction details"
                            >
                              View Transactions
                            </button>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {/* New toggle button that opens modal */}
                            <button
                              onClick={() => handleOpenToggleFlagModal(account)}
                              disabled={flagLoading === account.accountNumber}
                              className={`p-1 rounded-md ${
                                account.isFlagged
                                  ? "text-green-600 hover:text-green-800"
                                  : "text-red-600 hover:text-red-800"
                              }`}
                              title={
                                account.isFlagged
                                  ? "Unflag account"
                                  : "Flag account"
                              }
                            >
                              {flagLoading === account.accountNumber ? (
                                <Spinner size="small" />
                              ) : account.isFlagged ? (
                                <CheckIcon className="h-5 w-5" />
                              ) : (
                                <FlagIcon className="h-5 w-5" />
                              )}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <Paginate
              nextPage={nextPage}
              pageIndex={pageIndex}
              totalCount={pageNumber}
              totalPages={totalPage}
              prevPage={prevPage}
              setPageIndex={handlePageChange}
            />
          </>
        )}

        <ReportFraudulentAccountModal
          isOpen={showReportModal}
          onClose={() => setShowReportModal(false)}
          onSubmit={handleReportAccount}
          loading={loading}
        />

        <TransactionDetailsModal
          isOpen={showTransactionsModal}
          onClose={() => setShowTransactionsModal(false)}
          transactions={selectedAccount?.transactionDetails}
          accountNumber={selectedAccount?.accountNumber}
        />

        {/* Toggle Flag Status Modal - For Flagging */}
        <ToggleFlagModal
          isOpen={toggleFlagModal.isOpen}
          onClose={() => setToggleFlagModal({
            isOpen: false,
            accountNumber: "",
            currentFlagStatus: false,
            adminReason: "",
            bankCode: "105",
            bankName: "",
            existingAdminReason: ""
          })}
          onSubmit={handleToggleFlag}
          loading={flagLoading === toggleFlagModal.accountNumber}
          accountNumber={toggleFlagModal.accountNumber}
          currentFlagStatus={toggleFlagModal.currentFlagStatus}
          adminReason={toggleFlagModal.adminReason}
          bankCode={toggleFlagModal.bankCode}
          bankName={toggleFlagModal.bankName}
          existingAdminReason={toggleFlagModal.existingAdminReason}
          onAdminReasonChange={(reason) => setToggleFlagModal(prev => ({
            ...prev,
            adminReason: reason
          }))}
        />

        {/* Unflag Confirmation Modal - For Unflagging */}
        <UnflagConfirmationModal
          isOpen={unflagConfirmationModal.isOpen}
          onClose={() => setUnflagConfirmationModal({
            isOpen: false,
            accountNumber: "",
            bankCode: "105",
            bankName: ""
          })}
          onSubmit={handleUnflagAccount}
          loading={flagLoading === unflagConfirmationModal.accountNumber}
          accountNumber={unflagConfirmationModal.accountNumber}
          bankName={unflagConfirmationModal.bankName}
        />
      </div>
    </Layout>
  );
};

export default FraudulentAccounts;